import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Form, Formik } from 'formik';
import { array, object, string } from 'yup';

import { Container, NextLink, Text } from '@/atoms';
import {
  Button,
  Checkbox,
  Input,
  TextArea,
  WithPaperBorder,
} from '@/molecules';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  pagePropsPartial,
  seo,
  sliderHero,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import { addFormData, FORMS_ERROS, sendCF7Form } from '@/lib/utils';
import { config } from '@/config';

import type { NextPageWithLayout } from 'pages/_app';

const Ship = dynamic(() => import('@/assets/images/coming-soon/ship.svg'), {
  ssr: false,
});

const Beer = dynamic(() => import('@/assets/images/coming-soon/beer.svg'), {
  ssr: false,
});

const Navidad: NextPageWithLayout = ({
  page: { hero, flexibleContent },
}: any) => {
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');

  const FORMS_ID = {
    name: 'promocion-navidad-name',
    email: 'promocion-navidad-email',
    empresa: 'promocion-navidad-empresa',
    message: 'promocion-navidad-message',
    acepto: 'promocion-navidad-acepto',
  };

  const initialValues = {
    'promocion-navidad-name': undefined,
    'promocion-navidad-email': undefined,
    'promocion-navidad-empresa': undefined,
    'promocion-navidad-message': undefined,
    'promocion-navidad-acepto': [],
  };

  const formSchema = object().shape({
    'promocion-navidad-name': string().required(FORMS_ERROS.required),
    'promocion-navidad-email': string()
      .email('Formato de email invalido')
      .required(FORMS_ERROS.required),
    'promocion-navidad-empresa': string(),
    'promocion-navidad-message': string().required(FORMS_ERROS.required),
    'promocion-navidad-acepto': array()
      .required('Acepta la política de privacidad y cookies')
      .min(1, 'Acepta la política de privacidad y cookies'),
  });

  return (
    <>
      {submitted ? (
        <div className="flex flex-wrap items-center justify-center gap-6 py-16 md:gap-11">
          {error ? (
            <>
              <Ship className="w-[300px]" />
              <div className="flex max-w-[400px] flex-col items-center gap-4 text-center md:items-start md:text-left">
                <h3 className="u-display u-display--s">¡Oops!</h3>
                <span>{message}</span>
                <Button
                  size="normal"
                  className="max-w-[inherit]"
                  onClick={() => {
                    setError(false);
                    setSubmitted(false);
                  }}
                >
                  VOLVER al formulario
                </Button>
              </div>
            </>
          ) : (
            <>
              <Beer className="w-[120px]" />
              <div className="flex max-w-[400px] flex-col items-center gap-4 text-center md:items-start md:text-left">
                <h3 className="u-display u-display--s">
                  ¡gracias por ESCRIBIRNOS!
                </h3>
                <span>{message}</span>
                <NextLink href={routes.index} passHref>
                  <Button
                    as="a"
                    size="normal"
                    onClick={() => {
                      setError(false);
                      setSubmitted(false);
                    }}
                  >
                    VOLVER A LA TIENDA
                  </Button>
                </NextLink>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <HeroSlider hero={hero} />
          <FlexibleContent type="Page" flexible={flexibleContent.flexible} />
          <WithPaperBorder
            border="inside"
            top
            bottom
            borderHeight={20}
            borderColor="#F8F7F4"
          >
            <Container
              wrapper
              backgroundColor="bg-primary-100"
              id="contact_form"
            >
              <div
                id="form"
                className="my-4 flex w-full flex-col items-center gap-y-2 text-center md:mb-4 md:mt-[75px]"
              >
                <p className="u-label u-label--overlline block text-secondary-500">
                  contacto
                </p>
                <p className="u-headline u-headline--h2">
                  Dinos qué productos te interesan
                </p>
                <p className="u-body u-body--m max-w-[480px] text-primary-600 md:u-body--l">
                  Te ayudaremos a encontrar el regalo perfecto para tu empresa.
                </p>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={formSchema}
                onSubmit={(data) => {
                  if (!config?.forms?.christmas) {
                    setError(true);
                    setMessage('Algo ha salido mal.');
                    return;
                  }

                  return sendCF7Form({
                    id: config.forms.christmas,
                    data: addFormData(FORMS_ID, data),
                  })
                    .then((response) => setMessage(response.message))
                    .catch((response) => {
                      setError(true);
                      setMessage(
                        response.message ??
                          'Algo ha salido mal. Por favor, vuelve a intentarlo.',
                      );
                    })
                    .finally(() => setSubmitted(true));
                }}
              >
                {({ errors, isSubmitting }) => (
                  <Form noValidate className="mx-auto mt-8 max-w-[508px]">
                    <div className="flex flex-col gap-y-4">
                      <Input
                        id={FORMS_ID.name}
                        name={FORMS_ID.name}
                        placeholder="Nombre *"
                        error={errors[FORMS_ID.name as keyof typeof errors]}
                      />

                      <Input
                        id={FORMS_ID.email}
                        name={FORMS_ID.email}
                        placeholder="Email *"
                        error={errors[FORMS_ID.email as keyof typeof errors]}
                        type="email"
                      />

                      <Input
                        id={FORMS_ID.empresa}
                        name={FORMS_ID.empresa}
                        placeholder="Empresa"
                        error={errors[FORMS_ID.empresa as keyof typeof errors]}
                      />

                      <TextArea
                        id={FORMS_ID.message}
                        name={FORMS_ID.message}
                        placeholder="Mensaje *"
                        rows={7}
                        error={errors[FORMS_ID.message as keyof typeof errors]}
                      />

                      <span className="u-body u-body--s mb-6 inline-block text-typo-tertiary">
                        *Campos obligatorios
                      </span>
                    </div>

                    <Text className="u-headline u-headline--h4 mt-8">
                      Información básica privacidad
                    </Text>

                    <Text className="u-body u-body--xs mt-2 text-typo-tertiary">
                      Responsable del Tratamiento: Hijos de Rivera S.A.U.;
                      finalidad del tratamiento: gestión de la consulta;
                      legitimación del tratamiento: ejecución de un
                      contrato/acuerdo; destinatarios: no se cederán datos,
                      salvo obligación legal; derechos: acceder, rectificar y
                      suprimir los datos, así como otros derechos, como se
                      explica en la política de privacidad. Más información en
                      la{' '}
                      <NextLink href={routes.legalNoticePrivacyPolicy}>
                        <a className="u-body u-body--xs text-secondary-500">
                          política de privacidad y cookies.
                        </a>
                      </NextLink>
                      .
                    </Text>

                    <Checkbox
                      id={FORMS_ID.acepto}
                      name={FORMS_ID.acepto}
                      className="u-label u-label--s mt-[19px]"
                      values={[
                        {
                          value: 'true',
                          label: (
                            <p className="max-w-[310px]">
                              Acepto la{' '}
                              <NextLink href={routes.legalNoticePrivacyPolicy}>
                                <a className="text-secondary-500">
                                  política de privacidad y cookies
                                </a>
                              </NextLink>
                            </p>
                          ),
                        },
                      ]}
                      error={errors[FORMS_ID.acepto as keyof typeof errors]}
                    />

                    <Button
                      variant="tertiary"
                      type="submit"
                      className="mb-10 mt-5"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    >
                      enviar
                    </Button>
                  </Form>
                )}
              </Formik>
            </Container>
          </WithPaperBorder>
        </>
      )}
    </>
  );
};

export async function getStaticProps() {
  const content = `
    page(id: $slug, idType: URI) {
      ${seo}
      hero:${sliderHero('Page')}
      flexibleContent: ${FlexibleContentBlocks('Page')}
    }
  `;

  const Query = PageQuery(pagePropsPartial, content);

  return await pagePropsServerTransformer({
    staticProps: await getInitialProps(Query, { slug: '/promociones/navidad' }),
    type: 'Page',
    path: '/promociones/navidad',
  });
}

Navidad.getLayout = getBaseLayout;

export default Navidad;
